import { Box, Card } from '@mui/material';
import { useEffect, useState } from 'react';
import HttpServices from 'src/services/httpService';
import { postsModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { useToaster } from 'src/utils/toaster/toasterContext';
import ImageConfigurationComponent from './image-configuration.component';
import ImageGenerationFooterComponent from './image-generation-footer.component';
import ImageGenerationTypeComponent from './image-generation-type.component';
import ImageEditConfigurationComponent from './image-edit-configuration.component';
import { useResellerLimitDialog } from 'src/components/reseller-limit-context/resellerLimitContext';

const GenerateImageComponent = ({ handleGeneratedPost, handleEditImage }) => {
  const { showToast } = useToaster();
  const { showResellerLimitDialog } = useResellerLimitDialog();

  //State Variables
  const [selectedPostGenerationType, setSelectedPostGenerationType] = useState('prompt_to_image');
  const [selectedAspectRatio, setSelectedAspectRatio] = useState('1x1');
  const [selectedStyleType, setSelectedStyleType] = useState('AUTO');
  const [isUseBrandDetails, setUseBrandDetails] = useState(true);
  const [imageColor, setImageColor] = useState('');
  const [blogUrl, setUrl] = useState('');
  const [numberOfImagesToGenerate, setNumberOfImagesToGenerate] = useState(1);
  const [prompt, setPrompt] = useState('');
  const [isGeneratingAIPrompt, setGeneratingAIPrompt] = useState(false);
  const [isGeneratingPost, setGeneratingPost] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [generatedImages, setGeneratedImages] = useState([]);
  const [brandDetails, setBrandDetails] = useState({});
  const [selectedModel, setSelectedModel] = useState(null);
  const [imageModels, setImageModels] = useState([]);

  useEffect(() => {
    getBrandPreference();
  }, []);

  const getBrandPreference = () => {
    let getProps = {
      url: postsModuleURL.getBrandPreference,
      successCallback,
      failureCallback,
    };
    HttpServices.Get(getProps);
  };

  const failureCallback = (message) => {
    message && showToast(message, 'e');
    setGeneratingPost(false);
    setGeneratingAIPrompt(false);
    handleGeneratedPost(generatedImages, false);
    if ( message && !showResellerLimitDialog(message)) {
      showToast(message, 'e');
    }
  };

  const successCallback = (data, message) => {
    setBrandDetails(data);
  };

  const handlePostGenerationType = (type) => {
    setSelectedPostGenerationType(type);
    setFormErrors({});
  };

  const checkForValidUrl = (value) => {
    let errorMessage = '';
    let errors = { ...formErrors };
    if (value) {
      if (!CommonHelper.isValidUrl(value)) {
        errorMessage = 'Url is not valid. Enter valid url (For eg: https://example.com)';
      }
    } else {
      errorMessage = 'Url is required';
    }
    if (errorMessage) {
      errors['blogUrl'] = errorMessage;
    } else {
      delete errors['blogUrl'];
    }
    setFormErrors({ ...errors });
    return errorMessage;
  };

  const generateImageSuccessCallback = (data, message) => {
    setGeneratingPost(false);
    setGeneratedImages(data?.media_data);
    handleGeneratedPost(data?.media_data, false);
    getBrandPreference();
  };

  const generateImage = () => {
    if (selectedPostGenerationType === 'prompt_to_image' && !prompt) {
      showToast('Please enter prompt to generate image', 'e');
      return;
    }
    if (selectedPostGenerationType === 'blog_to_image' && checkForValidUrl(blogUrl)) {
      return;
    }
    setGeneratingPost(true);
    handleGeneratedPost(generatedImages, true);
    let postProps = {
      url: postsModuleURL.generateImage,
      body: generateBodyProps,
      successCallback: generateImageSuccessCallback,
      failureCallback,
    };
    HttpServices.Post(postProps);
  };

  const generateAIPrompt = () => {
    let postProps = {
      url: postsModuleURL.generateAIPrompt,
      body: generateBodyProps,
      successCallback: generateAIPromptSuccessCallback,
      failureCallback,
    };
    HttpServices.Post(postProps);
  };

  const generateAIPromptSuccessCallback = (data, message) => {
    setGeneratingAIPrompt(false);
    setPrompt(data?.content?.length > 0 && data?.content[0]);
    getBrandPreference();
  };

  const handleFields = (value, name) => {
    switch (name.toLowerCase()) {
      case 'numberofimagestogenerate':
        setNumberOfImagesToGenerate(value);
        break;
      case 'imagecolor':
        setImageColor(value);
        break;
      case 'usebranddetails':
        setUseBrandDetails(value);
        break;
      case 'styletype':
        setSelectedStyleType(value);
        break;
      case 'aspectratio':
        setSelectedAspectRatio(value);
        break;
      case 'blogurl':
        setUrl(value);
        checkForValidUrl(value);
        break;
      case 'prompt':
        setPrompt(value);
        break;
      case 'generatingaiprompt':
        setGeneratingAIPrompt(value);
        generateAIPrompt();
        break;
      case 'generateimage':
        generateImage();
        break;
      default:
        break;
    }
  };

  const generateBodyProps = {
    post_generation_type: selectedPostGenerationType,
    prompt_to_image: {
      aspect_ratio: selectedAspectRatio,
      is_color_palette_enabled: imageColor ? true : false,
      color_palette: imageColor,
      prompt: prompt,
      style_type: selectedStyleType,
      use_brand_details: isUseBrandDetails,
      number_of_images_to_generate: numberOfImagesToGenerate,
      ...(selectedModel && { model: selectedModel.model, model_provider: selectedModel.model_provider }),
    },
    blog_to_image: {
      aspect_ratio: selectedAspectRatio,
      is_color_palette_enabled: imageColor ? true : false,
      color_palette: imageColor,
      prompt: prompt,
      style_type: selectedStyleType,
      use_brand_details: isUseBrandDetails,
      number_of_images_to_generate: numberOfImagesToGenerate,
      blog_to_url: blogUrl,
      ...(selectedModel && { model: selectedModel.model, model_provider: selectedModel.model_provider }),
    },
    image_to_image: {
      aspect_ratio: selectedAspectRatio,
      is_color_palette_enabled: imageColor ? true : false,
      color_palette: imageColor,
      prompt: prompt,
      style_type: selectedStyleType,
      use_brand_details: isUseBrandDetails,
      number_of_images_to_generate: numberOfImagesToGenerate,
      ...(selectedModel && { model: selectedModel.model, model_provider: selectedModel.model_provider }),
    },
    edit_image: {
      aspect_ratio: selectedAspectRatio,
      is_color_palette_enabled: imageColor ? true : false,
      color_palette: imageColor,
      prompt: prompt,
      style_type: selectedStyleType,
      use_brand_details: isUseBrandDetails,
      number_of_images_to_generate: numberOfImagesToGenerate,
      ...(selectedModel && { model: selectedModel.model, model_provider: selectedModel.model_provider }),
    },
  };

  return (
    <Box sx={{ height: '90vh', display: 'flex', flexDirection: 'column' }}>
      <Card sx={{ flex: 1, p: 3, overflow: 'auto' }}>
        <Box sx={{ mb: 3 }}>
          <ImageGenerationTypeComponent
            brandDetails={brandDetails}
            selectedPostGenerationType={selectedPostGenerationType}
            handlePostGenerationType={handlePostGenerationType}
          />
        </Box>
        <Box>
          {selectedPostGenerationType === 'edit_image' ? (
            <ImageEditConfigurationComponent handleEditImage={handleEditImage} />
          ) : (
            <ImageConfigurationComponent
              numberOfImagesToGenerate={numberOfImagesToGenerate}
              imageColor={imageColor}
              isUseBrandDetails={isUseBrandDetails}
              selectedStyleType={selectedStyleType}
              selectedAspectRatio={selectedAspectRatio}
              selectedPostGenerationType={selectedPostGenerationType}
              formErrors={formErrors}
              blogUrl={blogUrl}
              handleFields={handleFields}
              selectedModel={selectedModel}
              setSelectedModel={setSelectedModel}
              imageModels={imageModels}
            />
          )}
        </Box>
      </Card>

      {selectedPostGenerationType !== 'edit_image' && (
        <Box
          sx={{
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            position: 'sticky',
            bottom: 0,
            zIndex: 1,
          }}
        >
          <ImageGenerationFooterComponent
            prompt={prompt}
            isGeneratingAIPrompt={isGeneratingAIPrompt}
            isGeneratingPost={isGeneratingPost}
            handleFields={handleFields}
          />
        </Box>
      )}
    </Box>
  );
};

export default GenerateImageComponent;
